'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from 'src/components/PulseUI';
import { ENCOUNTERS_DEFINITION } from 'src/utils/encounterLabels';

interface EncountersInfoTooltipProps {
  className?: string;
  'data-testid'?: string;
}

/**
 * The definition is a full sentence, so it needs room to stay legible.
 *
 * This sets an explicit **width**, not a `max-w-*`. PulseUI's Tooltip container is
 * `w-fit` on a `position: fixed` element with only `left` set, so its available width is
 * `viewport - left` and `fit-content` clamps to it — a `max-w-*` on this inner div only
 * caps the width and cannot stop that clamp, which collapses the bubble wherever the
 * trigger sits near a right edge (the volume drawer especially). A definite width on the
 * content makes the container's shrink-to-fit resolve to it instead.
 *
 * `w-112` (448px) is the repo's existing width token; `max-w-[90vw]` keeps it on-screen
 * on narrow viewports.
 */
const TOOLTIP_CONTENT_CLASS =
  'w-112 max-w-[90vw] whitespace-normal text-left leading-relaxed';

/**
 * Info icon that explains what an encounter is on hover/focus. Rendered next to the
 * prominent "Encounters" labels (Discovery results, volume type toggle) so users
 * understand the volume difference where it is most visible.
 */
const EncountersInfoTooltip: React.FC<EncountersInfoTooltipProps> = ({
  className = '',
  'data-testid': dataTestId = 'encounters-info-tooltip',
}) => (
  <Tooltip
    content={ENCOUNTERS_DEFINITION}
    // `left` anchors the bubble's right edge to the icon so it grows leftward into
    // available space. PulseUI's Tooltip has no viewport collision/flip logic, so the
    // default `top` centres this wide bubble on the icon and runs off the right edge
    // wherever the label sits right-of-centre (Discovery stats, the volume drawer).
    position="left"
    className={`inline-flex items-center ${className}`}
    slotProps={{ contentClassName: TOOLTIP_CONTENT_CLASS }}
  >
    {/* Mirrors the info-icon pattern used by LicenseOverview / ColumnHeader: the label
        lives on the wrapper so the icon itself stays decorative, and it carries the
        definition itself — PulseUI's Tooltip renders its bubble in a portal with no
        `aria-describedby` link, so the copy would otherwise never reach a screen reader.
        `tabIndex` makes it reachable by keyboard via the Tooltip's focus handler. */}
    <span
      className="inline-flex"
      role="img"
      aria-label={ENCOUNTERS_DEFINITION}
      tabIndex={0}
      data-testid={dataTestId}
    >
      <Icon
        icon={faCircleInfo}
        className="text-xs text-neutral-400 hover:text-neutral-600"
        aria-hidden="true"
      />
    </span>
  </Tooltip>
);

export default EncountersInfoTooltip;
